import { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { Card, Col, Form, Row, Tab, Tabs } from 'react-bootstrap';
import Common from '../../../shared/Common';

import { DictionaryContext } from '../../../../App';
import PredictionStatistics from './PredictionStatistics';
import { useLocalStorage } from 'react-use';
import { ExpenseCategoryChart, FinancialChart, FinancialTable } from '.';
import type { IExpenseCategoryData, IGenericFinancialValue } from './types';
import { tableDataGreenColor } from './colors';
import { prepareExpenseCategoryData, prepareFinancialData } from './chartUtils';
import { ICategory } from '../../../../interfaces/ICategory';

const FinancialStatistics = () => {
  const [weeklyFinancialData, setWeeklyFinancialData] = useState<IGenericFinancialValue[] | undefined>(undefined);
  const [monthlyFinancialData, setMonthlyFinancialData] = useState<IGenericFinancialValue[] | undefined>(undefined);
  const [expenseCategoryData, setExpenseCategoryData] = useState<IExpenseCategoryData[] | undefined>(undefined);
  const [allCategories, setAllCategories] = useState<ICategory[] | undefined>(undefined);
  const [includeFuture, setIncludeFuture] = useLocalStorage<boolean>('FinancialStatistics.includeFuture', false);
  const [includeTaxes, setIncludeTaxes] = useLocalStorage<boolean>('FinancialStatistics.includeTaxes', true);
  const [includeCurrentMonth, setIncludeCurrentMonth] = useLocalStorage<boolean>(
    'FinancialStatistics.includeCurrentMonth',
    false
  );
  const [showLastMonths, setShowLastMonths] = useLocalStorage<boolean>('FinancialStatistics.showLastMonths', true);
  const [lastMonthsCount, setLastMonthsCount] = useLocalStorage<number>('FinancialStatistics.lastMonthsCount', 12);
  const [groupByYears, setGroupByYears] = useLocalStorage<boolean>('FinancialStatistics.groupByYears', false);
  const [showMonthlyIncomes, setShowMonthlyIncomes] = useLocalStorage<boolean>(
    'FinancialStatistics.showMonthlyIncomes',
    false
  );
  const [showWeeklyIncomes, setShowWeeklyIncomes] = useLocalStorage<boolean>(
    'FinancialStatistics.showWeeklyIncomes',
    false
  );
  const [showMonthlyExpenses, setShowMonthlyExpenses] = useLocalStorage<boolean>(
    'FinancialStatistics.showMonthlyExpenses',
    true
  );
  const [showMonthlyExpensesCategories, setShowMonthlyExpensesCategories] = useLocalStorage<boolean>(
    'FinancialStatistics.showMonthlyExpensesCategories',
    true
  );
  const [showWeeklyExpenses, setShowWeeklyExpenses] = useLocalStorage<boolean>(
    'FinancialStatistics.showWeeklyExpenses',
    true
  );
  const [showWeeklyExpensesCategories, setShowWeeklyExpensesCategories] = useLocalStorage<boolean>(
    'FinancialStatistics.showWeeklyExpensesCategories',
    true
  );
  const [showMonthlyBalance, setShowMonthlyBalance] = useLocalStorage<boolean>(
    'FinancialStatistics.showMonthlyBalance',
    false
  );
  const [showWeeklyBalance, setShowWeeklyBalance] = useLocalStorage<boolean>(
    'FinancialStatistics.showWeeklyBalance',
    false
  );
  const [showMonthlyTaxes, setShowMonthlyTaxes] = useLocalStorage<boolean>(
    'FinancialStatistics.showMonthlyTaxes',
    false
  );
  const [showWeeklyTaxes, setShowWeeklyTaxes] = useLocalStorage<boolean>('FinancialStatistics.showWeeklyTaxes', false);
  const [showReferenceLines, setShowReferenceLines] = useLocalStorage<boolean>(
    'FinancialStatistics.showReferenceLines',
    false
  );
  const [showMonthlyChart, setShowMonthlyChart] = useLocalStorage<boolean>(
    'FinancialStatistics.showMonthlyChart',
    true
  );
  const [showWeeklyChart, setShowWeeklyChart] = useLocalStorage<boolean>('FinancialStatistics.showWeeklyChart', false);
  const [showSums, setShowSums] = useLocalStorage<boolean>('FinancialStatistics.showSums', true);
  const [showPredictions, setShowPredictions] = useLocalStorage<boolean>('FinancialStatistics.showPredictions', false);
  const [balanceTarget, setBalanceTarget] = useState(0);
  const [chartHeight, setChartHeight] = useState(400);

  const fetchWeeklyFinancialDataIdRef = useRef(0);
  const fetchMonthlyFinancialDataRef = useRef(0);
  const fetchExpenseCategoryDataRef = useRef(0);

  const { userPreferences } = useContext(DictionaryContext);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1000 && window.innerWidth > window.innerHeight) {
        let mobileHeight = window.innerHeight - 100;
        if (mobileHeight <= 250) {
          mobileHeight = 250;
        }
        setChartHeight(mobileHeight);
      } else {
        setChartHeight(400);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (userPreferences?.monthlyTargetBalance) {
      setBalanceTarget(userPreferences?.monthlyTargetBalance);
    }
  }, [userPreferences]);

  const fetchWeeklyFinancialData = useCallback(async () => {
    const fetchId = ++fetchWeeklyFinancialDataIdRef.current;

    const response = await Common.authorizedFetch('api/statistics/GetWeeklyFinancialSummary');
    if (fetchId === fetchWeeklyFinancialDataIdRef.current) {
      const data = await response.json();
      if (data.success) {
        setWeeklyFinancialData(data.result.data);
      }
    }
  }, []);

  const fetchMonthlyFinancialData = useCallback(async () => {
    const fetchId = ++fetchMonthlyFinancialDataRef.current;

    const response = await Common.authorizedFetch('api/statistics/GetMonthlyFinancialSummary');
    if (fetchId === fetchMonthlyFinancialDataRef.current) {
      const data = await response.json();
      if (data.success) {
        setMonthlyFinancialData(data.result.data);
      }
    }
  }, []);

  const fetchExpenseCategoryData = useCallback(async () => {
    const fetchId = ++fetchExpenseCategoryDataRef.current;

    const response = await Common.authorizedFetch('api/statistics/GetExpenseCategoryData');
    if (fetchId === fetchExpenseCategoryDataRef.current) {
      const data = await response.json();
      if (data.success) {
        setExpenseCategoryData(data.result.data);
        setAllCategories(data.result.allUserCategories);
      }
    }
  }, []);

  useEffect(() => {
    fetchWeeklyFinancialData();
    fetchMonthlyFinancialData();
    fetchExpenseCategoryData();
  }, [fetchMonthlyFinancialData, fetchWeeklyFinancialData, fetchExpenseCategoryData]);

  const generateFinancialData = useCallback(
    (financialData: IGenericFinancialValue[] | undefined, type: string) => {
      const isWeekly = type === 'weekly';
      const showChart = isWeekly ? showWeeklyChart : showMonthlyChart;
      const setShowChart = isWeekly ? setShowWeeklyChart : setShowMonthlyChart;
      const showExpenses = isWeekly ? showWeeklyExpenses : showMonthlyExpenses;
      const showIncomes = isWeekly ? showWeeklyIncomes : showMonthlyIncomes;
      const showBalance = isWeekly ? showWeeklyBalance : showMonthlyBalance;
      const showTaxes = isWeekly ? showWeeklyTaxes : showMonthlyTaxes;
      const setShowExpenses = isWeekly ? setShowWeeklyExpenses : setShowMonthlyExpenses;
      const setShowIncomes = isWeekly ? setShowWeeklyIncomes : setShowMonthlyIncomes;
      const setShowBalance = isWeekly ? setShowWeeklyBalance : setShowMonthlyBalance;
      const setShowTaxes = isWeekly ? setShowWeeklyTaxes : setShowMonthlyTaxes;

      const generateChartData = (data: IGenericFinancialValue[]) => {
        const chartData = prepareFinancialData(data, {
          includeFuture,
          includeCurrentMonth,
          showLastMonths,
          lastMonthsCount,
          includeTaxes,
          isWeekly,
        });

        return (
          <FinancialChart
            data={chartData}
            isWeekly={isWeekly}
            showReferenceLines={showReferenceLines}
            showExpenses={showExpenses}
            showIncomes={showIncomes}
            showBalance={showBalance}
            showTaxes={showTaxes}
            includeTaxes={includeTaxes}
            showSums={showSums}
            groupByYears={groupByYears}
            balanceTarget={balanceTarget}
            chartHeight={chartHeight}
            setShowExpenses={setShowExpenses}
            setShowIncomes={setShowIncomes}
            setShowBalance={setShowBalance}
            setShowTaxes={setShowTaxes}
          />
        );
      };

      const generateTableData = (data: IGenericFinancialValue[]) => {
        return (
          <FinancialTable
            data={data}
            isWeekly={isWeekly}
            includeTaxes={includeTaxes}
            includeFuture={includeFuture}
            groupByYears={groupByYears}
          />
        );
      };

      let cardBody: React.JSX.Element;
      if (financialData === undefined) {
        cardBody = <div style={{ height: 200 }}>{Common.Ui.showLoadingSpinnerAbsolute(true)}</div>;
      } else {
        if (financialData.length === 0) {
          return <></>;
        } else {
          if (!groupByYears) {
            if (showChart) {
              cardBody = generateChartData(financialData);
            } else {
              cardBody = generateTableData(financialData);
            }
          } else {
            const tabs: React.JSX.Element[] = [];
            const currentYear = new Date().getFullYear();
            const yearGroup = Common.Utils.groupBy(financialData, (i) => i.year);
            const yearsDescending = Object.keys(yearGroup)
              .filter((e) => includeFuture || Number(e) <= currentYear)
              .map((e) => Number(e))
              .sort()
              .reverse();
            for (let ind = 0; ind < yearsDescending.length; ind++) {
              const year = yearsDescending[ind];
              const yearData = yearGroup[year];
              if (showChart) {
                tabs.push(
                  <Tab key={ind + '-' + year} eventKey={ind + '-' + year} title={year}>
                    {generateChartData(yearData)}
                  </Tab>
                );
              } else {
                if (yearData.length > 0) {
                  tabs.push(
                    <Tab key={ind + '-' + year} eventKey={ind + '-' + year} title={year}>
                      {generateTableData(yearData)}
                    </Tab>
                  );
                }
              }
            }
            cardBody = (
              <Tabs key='' className='mb-3'>
                {tabs}
              </Tabs>
            );
          }
        }
      }

      return (
        <Card className='mt-3 mb-2'>
          <Card.Header className='statistics-card-header'>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>{isWeekly ? 'Statystyki tygodniowe' : 'Statystyki miesięczne'}</div>
              <div>
                <Form.Check
                  type='switch'
                  id={`show-chart-${type}-switch`}
                  label='Prezentuj wykres'
                  style={{ userSelect: 'none' }}
                  checked={showChart}
                  onChange={() => {
                    setShowChart(!showChart);
                  }}
                />
              </div>
            </div>
          </Card.Header>
          <Card.Body className='pb-0 pt-2'>{cardBody}</Card.Body>
        </Card>
      );
    },
    [
      includeCurrentMonth,
      includeFuture,
      includeTaxes,
      groupByYears,
      showMonthlyChart,
      showWeeklyChart,
      showWeeklyBalance,
      showMonthlyBalance,
      showWeeklyExpenses,
      showWeeklyExpensesCategories,
      showMonthlyExpenses,
      showMonthlyExpensesCategories,
      showWeeklyIncomes,
      showMonthlyIncomes,
      showWeeklyTaxes,
      showMonthlyTaxes,
      showReferenceLines,
      chartHeight,
      showLastMonths,
      lastMonthsCount,
      showSums,
      balanceTarget,
      setShowMonthlyBalance,
      setShowMonthlyChart,
      setShowMonthlyExpenses,
      setShowMonthlyExpensesCategories,
      setShowMonthlyIncomes,
      setShowMonthlyTaxes,
      setShowWeeklyBalance,
      setShowWeeklyChart,
      setShowWeeklyExpenses,
      setShowWeeklyExpensesCategories,
      setShowWeeklyIncomes,
      setShowWeeklyTaxes,
    ]
  );

  const generateExpenseCategory = useCallback(
    (financialData: IExpenseCategoryData[] | undefined) => {
      const generateChartData = (data: IExpenseCategoryData[]) => {
        const chartData = prepareExpenseCategoryData(data, {
          includeFuture,
          includeCurrentMonth,
          showLastMonths,
          lastMonthsCount,
        });

        return (
          <ExpenseCategoryChart
            data={chartData}
            allCategories={allCategories ?? []}
            showReferenceLines={showReferenceLines}
            chartHeight={chartHeight + 150}
            includeTaxes={includeTaxes}
          />
        );
      };
      let cardBody: React.JSX.Element;
      if (financialData === undefined) {
        cardBody = <div style={{ height: 200 }}>{Common.Ui.showLoadingSpinnerAbsolute(true)}</div>;
      } else {
        if (financialData.length === 0) {
          return <></>;
        } else {
          if (!groupByYears) {
            cardBody = generateChartData(financialData);
          } else {
            const tabs: React.JSX.Element[] = [];
            const currentYear = new Date().getFullYear();
            const yearGroup = Common.Utils.groupBy(financialData, (i) => i.year);
            const yearsDescending = Object.keys(yearGroup)
              .filter((e) => includeFuture || Number(e) <= currentYear)
              .map((e) => Number(e))
              .sort()
              .reverse();
            for (let ind = 0; ind < yearsDescending.length; ind++) {
              const year = yearsDescending[ind];
              const yearData = yearGroup[year];
              tabs.push(
                <Tab key={ind + '-' + year} eventKey={ind + '-' + year} title={year}>
                  {generateChartData(yearData)}
                </Tab>
              );
            }
            cardBody = (
              <Tabs key='' className='mb-3'>
                {tabs}
              </Tabs>
            );
          }
        }
      }

      return (
        <Card className='mt-3 mb-2'>
          <Card.Header className='statistics-card-header'>
            <div>Kategorie wydatków</div>
          </Card.Header>
          <Card.Body className='pb-0 pt-2'>{cardBody}</Card.Body>
        </Card>
      );
    },
    [
      includeCurrentMonth,
      includeFuture,
      includeTaxes,
      groupByYears,
      showMonthlyChart,
      showWeeklyChart,
      showWeeklyBalance,
      showMonthlyBalance,
      showWeeklyExpenses,
      showWeeklyExpensesCategories,
      showMonthlyExpenses,
      showMonthlyExpensesCategories,
      showWeeklyIncomes,
      showMonthlyIncomes,
      showWeeklyTaxes,
      showMonthlyTaxes,
      showReferenceLines,
      chartHeight,
      showLastMonths,
      lastMonthsCount,
      showSums,
      balanceTarget,
      allCategories,
      setShowMonthlyBalance,
      setShowMonthlyChart,
      setShowMonthlyExpenses,
      setShowMonthlyExpensesCategories,
      setShowMonthlyIncomes,
      setShowMonthlyTaxes,
      setShowWeeklyBalance,
      setShowWeeklyChart,
      setShowWeeklyExpenses,
      setShowWeeklyExpensesCategories,
      setShowWeeklyIncomes,
      setShowWeeklyTaxes,
    ]
  );

  const switchesDisabled = !showMonthlyChart && !showWeeklyChart;

  return (
    <>
      {weeklyFinancialData?.length !== 0 && monthlyFinancialData?.length !== 0 && (
        <div
          className='mt-3 px-3 py-3'
          style={{
            borderRadius: '5px',
            backgroundColor: tableDataGreenColor,
            marginBottom: '15px',
            color: 'white',
          }}
        >
          <Row className='mt-2'>
            <Col xs={6} md={4}>
              <Form.Check
                type='switch'
                id='include-taxes-switch'
                style={{ userSelect: 'none' }}
                label='Uwzględnij podatki'
                checked={includeTaxes}
                onChange={() => {
                  setIncludeTaxes(!includeTaxes);
                }}
              />
            </Col>
            <Col xs={6} md={4}>
              <Form.Check
                disabled={switchesDisabled}
                type='switch'
                id='include-future-switch'
                style={{ userSelect: 'none' }}
                label='Uwzględnij przyszłe'
                checked={includeFuture}
                onChange={() => {
                  if (includeCurrentMonth && !includeFuture) {
                    setIncludeCurrentMonth(false);
                  }
                  setIncludeFuture(!includeFuture);
                }}
              />
            </Col>
            <Col xs={6} md={4}>
              <Form.Check
                disabled={switchesDisabled}
                type='switch'
                id='include-current-month-switch'
                style={{ userSelect: 'none' }}
                label='Uwzględnij bieżący miesiąc'
                checked={includeCurrentMonth}
                onChange={() => {
                  if (!includeCurrentMonth && includeFuture) {
                    setIncludeFuture(false);
                  }
                  setIncludeCurrentMonth(!includeCurrentMonth);
                }}
              />
            </Col>
            <Col xs={6} md={4}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Form.Check
                  disabled={switchesDisabled}
                  type='switch'
                  id='show-last-months-switch'
                  style={{ userSelect: 'none' }}
                  label='Pokaż ostatnie miesiące'
                  checked={showLastMonths}
                  onChange={() => {
                    if (!showLastMonths && groupByYears) {
                      setGroupByYears(false);
                    }
                    setShowLastMonths(!showLastMonths);
                  }}
                />
                <Form.Control
                  disabled={switchesDisabled || !showLastMonths}
                  value={lastMonthsCount}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    if (!isNaN(value) && value >= 1 && value <= 120) {
                      setLastMonthsCount(value);
                    }
                  }}
                  style={{
                    height: '26px',
                    maxWidth: '40px',
                    fontSize: '14px',
                    padding: '0px 0px 0px 5px',
                  }}
                />
              </div>
            </Col>
            <Col xs={6} md={4}>
              <Form.Check
                disabled={switchesDisabled}
                type='switch'
                id='group-by-year-switch'
                style={{ userSelect: 'none' }}
                label='Grupuj po latach'
                checked={groupByYears}
                onChange={() => {
                  if (showLastMonths && !groupByYears) {
                    setShowLastMonths(false);
                  }
                  setGroupByYears(!groupByYears);
                }}
              />
            </Col>
            <Col xs={6} md={4}>
              <Form.Check
                disabled={switchesDisabled}
                type='switch'
                id='show-reference-lines-switch'
                style={{ userSelect: 'none' }}
                label='Wyświetl średnie'
                checked={showReferenceLines}
                onChange={() => {
                  setShowReferenceLines(!showReferenceLines);
                }}
              />
            </Col>
            <Col xs={6} md={4}>
              <Form.Check
                disabled={switchesDisabled}
                type='switch'
                id='show-sums-switch'
                style={{ userSelect: 'none' }}
                label='Wyświetl sumę'
                checked={showSums}
                onChange={() => {
                  setShowSums(!showSums);
                }}
              />
            </Col>
            <Col xs={6} md={4}>
              <Form.Check
                disabled={switchesDisabled}
                type='switch'
                id='show-predictions-switch'
                style={{ userSelect: 'none' }}
                label='Wyświetl przewidywania'
                checked={showPredictions}
                onChange={() => {
                  setShowPredictions(!showPredictions);
                }}
              />
            </Col>
          </Row>
        </div>
      )}
      <PredictionStatistics
        chartHeight={chartHeight}
        showPredictions={showPredictions!}
        monthlyFinancialData={monthlyFinancialData}
      />
      {generateFinancialData(monthlyFinancialData, 'monthly')}
      {generateFinancialData(weeklyFinancialData, 'weekly')}
      {generateExpenseCategory(expenseCategoryData)}
    </>
  );
};

export default FinancialStatistics;
